import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { first, switchMap, timeout } from 'rxjs/operators';
import { FirebaseAuthService } from '../firebase/firebaseAuth.service';
import { environment } from '../../environments/environment';

export interface ReportRow {
  dimensions: string[];
  metrics: number[];
}

export interface AnalyticsReport {
  dimensionNames: string[];
  metricNames: string[];
  rows: ReportRow[];
  /** The same rows for the window before this one. Empty for "all time". */
  previousRows: ReportRow[];
}

/** One place and device group left out of every report. */
export interface ExcludedGroup {
  city: string;
  /** State or province. GA4 leaves this unset outside some countries. */
  region: string;
  country: string;
  operatingSystem: string;
  users: number;
  engagedVisits: number;
  /** Seconds of engagement each person recorded. Machines run under one. */
  secondsPerUser: number;
}

export interface AnalyticsResponse {
  generatedAt: string;
  propertyId: string;
  range: string;
  /** Groups dropped from every report because their traffic is automated. */
  excludedPlaces: ExcludedGroup[];
  /** The two periods the comparison card ran over. */
  comparison: {
    firstStart: string;
    firstEnd: string;
    secondStart: string;
    secondEnd: string;
  };
  startDate: string;
  endDate: string;
  previousStartDate: string;
  previousEndDate: string;
  reports: { [key: string]: AnalyticsReport };
}

/** Nothing on this page is worth waiting longer than this for. */
const REQUEST_TIMEOUT_MS = 30000;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly url = `${environment.functionsUrl}/getAnalyticsReport`;

  constructor(
    private http: HttpClient,
    private authService: FirebaseAuthService
  ) {}

  /**
   * Reads the usage numbers. The function checks the token again on the
   * server, so the browser guard is not the only protection.
   *
   * It waits on idToken rather than reading authService.user. On a direct
   * page load Firebase has not restored the session yet, so that property is
   * still undefined and the page would wait for a call that never happened.
   */
  getReport(
    range: string,
    includeAutomated: boolean,
    start?: string,
    end?: string,
    compare?: { aStart?: string; aEnd?: string; bStart?: string; bEnd?: string }
  ): Observable<AnalyticsResponse> {
    const dates = compare || {};
    const comparison = (['aStart', 'aEnd', 'bStart', 'bEnd'] as const)
      .filter((name) => dates[name])
      .map((name) => `&${name}=${dates[name]}`)
      .join('');
    // timeout goes after switchMap. Before it, it bounds only the wait for
    // the token, and the report request itself would run without a limit.
    return this.authService.afAuth.idToken.pipe(
      first((token): token is string => !!token),
      switchMap((token) =>
        this.http.get<AnalyticsResponse>(
          `${this.url}?range=${range}` +
            (includeAutomated ? '&automated=include' : '') +
            (start && end ? `&start=${start}&end=${end}` : '') +
            comparison,
          {
            headers: new HttpHeaders({ Authorization: `Bearer ${token}` }),
          }
        )
      ),
      timeout(REQUEST_TIMEOUT_MS)
    );
  }
}
